import APIConfig from "service/network/APIConfig";
import APIService from "service/network/APIService";
import Helper from "util/Helper";

/**
 * Primary-therapy assessment — the patient's main condition, and the one thing
 * about it a care manager can change from the record header.
 *
 * Served by pa-service on serviceAPI, NOT baseAPI. Three calls, and the shapes
 * do not agree with each other, which is the main thing to know here:
 *
 *   list    GET  /v2/pa/assessments/questions/1  -> { fields: [ … ] }
 *   current GET  /v2/pa/assessments/{pid}/primary -> { primaryTherapy: 2, answer: "2" }
 *   change  POST /v2/pa/assessments/{pid}          <- { answer: "2", questionId: "1" }
 *
 * `answer` is a STRING on the way in and out; `primaryTherapy` is an int. The
 * two identify the same therapy, so everything here compares them as strings.
 */
/**
 * The master list, cached for the tab.
 *
 * It is PATIENT-AGNOSTIC and effectively static, but two things want it on the
 * same screen — the header resolves the current code to a name on mount, and
 * the picker needs the full list when its menu opens. Without this they each
 * fetch it, on every patient page. In-flight calls share one promise so an
 * open-while-loading does not double up either.
 */
let listCache = null;
let listInFlight = null;

class TherapyService {
  constructor() {
    this.apiService = new APIService();
    // Side effect: installs String.prototype.format for the URL templates.
    this.helper = new Helper();
  }

  /**
   * The master list of therapies.
   *
   * Each field is normalised to `{ tag, text }`. The two apps that already read
   * this endpoint disagree about the key — phr matched on `f.id`, bridge's old
   * screen rendered `e.tag` — so both are accepted and whichever is present
   * wins. Getting that wrong does not error; it silently shows the wrong
   * therapy, which is worse.
   */
  list(onResponse, onError) {
    if (listCache) {
      onResponse(listCache);
      return undefined;
    }
    if (listInFlight) {
      listInFlight.push({ onResponse, onError });
      return undefined;
    }
    listInFlight = [{ onResponse, onError }];
    const settle = (fn, arg) => {
      const waiting = listInFlight || [];
      listInFlight = null;
      waiting.forEach((w) => w[fn] && w[fn](arg));
    };

    return this.apiService.get(
      APIConfig.patientAssessment.therapyQuestions,
      (res) => {
        const fields = (res && res.fields) || [];
        const rows = fields
          .map((f) => ({
            tag: String(f.tag != null ? f.tag : f.id),
            text: String(f.text || "").trim(),
          }))
          .filter((f) => f.tag && f.tag !== "undefined" && f.text);
        // Only a NON-EMPTY list is cached — an empty one is a failure wearing
        // a 200, and caching it would poison the picker for the whole tab.
        if (rows.length) listCache = rows;
        settle("onResponse", rows);
      },
      (err) => settle("onError", err)
    );
  }

  /** The patient's current therapy, as a string tag ("" when none is set). */
  current(patientId, onResponse, onError) {
    const url = APIConfig.patientAssessment.primaryTherapy.format(patientId);
    return this.apiService.get(
      url,
      (res) => {
        if (!res) return onResponse("");
        const raw = res.primaryTherapy != null ? res.primaryTherapy : res.answer;
        onResponse(raw == null || raw === "" ? "" : String(raw));
      },
      onError
    );
  }

  /**
   * Set the patient's primary therapy.
   *
   * `questionId: "1"` is not optional — the endpoint is a generic assessment
   * write and 1 is the primary-therapy question. Both values are strings on the
   * wire, matching what bridge's original screen sent.
   *
   * `post`, NOT `postAgain`, and deliberately: post() RETRIES once on failure
   * (newAPIService.post falls through to postAgain). That is wrong for a call
   * note, which creates a row per call and duplicated on retry — but this write
   * is idempotent, setting the same therapy twice is the same therapy, so the
   * retry is a free second chance at a flaky network. A caller only sees
   * onError once BOTH attempts have failed.
   */
  change(patientId, tag, onResponse, onError) {
    const url = APIConfig.patientAssessment.changeTherapy.format(patientId);
    return this.apiService.post(
      url,
      { answer: String(tag), questionId: "1" },
      onResponse,
      onError
    );
  }
  /**
   * The patient's expected delivery date, or "" when none is recorded.
   * Answers "" on failure too — a missing date and an unreachable service lead
   * to the same place here (ask for one), and distinguishing them would only
   * let a network blip block the therapy change.
   */
  deliveryDate(patientId, onResponse, onError) {
    const url = APIConfig.patientAssessment.deliveryDateRead.format(patientId);
    return this.apiService.get(
      url,
      (res) => onResponse((res && res.deliveryDate) || ""),
      onError
    );
  }

  /**
   * Record a first delivery date. `dueDate` is "YYYY-MM-DD".
   *
   * BARE date, no time suffix — bridge's add path sends exactly this, and its
   * update path sends the suffixed form. See `updateDeliveryDate`.
   */
  addDeliveryDate(patientId, dueDate, onResponse, onError) {
    return this.apiService.post(
      APIConfig.patientAssessment.deliveryDateWrite,
      { deliveryDate: dueDate, userId: Number(patientId) },
      onResponse,
      onError
    );
  }

  /**
   * Replace an existing delivery date. Same url, PUT.
   *
   * SUFFIXED with T00:00:00.000Z, unlike `addDeliveryDate`. bridge carries a
   * commented-out `moment(dueDate)` above this line, so the shape was arrived
   * at by trial; it is copied rather than normalised because nothing available
   * here can prove the endpoint accepts the bare form on this verb.
   */
  updateDeliveryDate(patientId, dueDate, onResponse, onError) {
    return this.apiService.put(
      APIConfig.patientAssessment.deliveryDateWrite,
      { deliveryDate: `${dueDate}T00:00:00.000Z`, userId: Number(patientId) },
      onResponse,
      onError
    );
  }

  /**
   * The account's minor members. Answers an ARRAY, empty when there are none.
   * New born care requires at least one.
   */
  minorMembers(patientId, onResponse, onError) {
    return this.apiService.post(
      APIConfig.patientAssessment.minorMembers,
      { patientIds: [Number(patientId)] },
      (res) => onResponse(Array.isArray(res) ? res : (res && res.members) || []),
      onError
    );
  }
}

export default TherapyService;
